import { ProviderIntegrationApi } from "../service/Api/ProviderIntegrationApi";
import {
  AUTH_KEY,
  DETAIL_KEY,
  KEY_TYPE,
  REQUEST_BODY_KEY,
  REQUEST_URL_KEY,
} from "../constants/integrationKeys";
import {
  AuthTypeApi,
  IntegrationTypeParam,
  mapAuthTypeIdToApiId,
  mapIntegrationToStageConfig,
  PAConfigurationApi,
  PAConfigurationKeyValueApi,
  SavePAConfigurationRequest,
} from "../pages/payerIntegration/types/providerIntegrationtypes";
import { AuthTypeId, HttpMethodId, StageConfig, StageKey } from "./integrationConfigData";
import { KeyValueItem } from "./keyValueItems";
import { buildTestResultDisplayMessage } from "./testResultMessage";

export class ConfigurationNotFoundError extends Error {
  constructor(message = "Configuration not found") {
    super(message);
    this.name = "ConfigurationNotFoundError";
  }
}

const isNotFound = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export const loadStageConfigByAuthType = async (
  providerId: number,
  stageKey: StageKey,
  authTypeId: number,
  configurationTypeId?: number,
): Promise<StageConfig & { configurationId?: number }> => {
  try {
    const api = await ProviderIntegrationApi.getIntegration(
      providerId,
      stageKey,
      authTypeId,
      configurationTypeId,
    );
    return {
      ...mapIntegrationToStageConfig(api),
      configurationId: api.id,
    };
  } catch (error: unknown) {
    if (isNotFound(error)) {
      return { status: "notSet", description: "" };
    }
    throw error instanceof Error
      ? error
      : new Error("Unable to load configuration");
  }
};

/** Single GET for the active configuration (+ optional configurationTypeId). */
export const loadStageConfig = async (
  providerId: number,
  stageKey: StageKey,
  configurationTypeId?: number,
): Promise<StageConfig & { configurationId?: number }> => {
  try {
    const api = await ProviderIntegrationApi.getIntegration(
      providerId,
      stageKey,
      undefined,
      configurationTypeId,
    );
    return {
      ...mapIntegrationToStageConfig(api),
      configurationId: api.id,
    };
  } catch (error: unknown) {
    if (isNotFound(error)) {
      return { status: "notSet", description: "" };
    }
    throw error instanceof Error
      ? error
      : new Error("Unable to load configuration");
  }
};

export interface EndpointFormPayload {
  endpoint: string;
  authType: AuthTypeId;
  requestUrls: KeyValueItem[];
  /** Connection headers → details (keyType Header). */
  headers: KeyValueItem[];
  /** Credential headers → credentials (keyType Header). */
  credentialHeaders?: KeyValueItem[];
  queryParams: KeyValueItem[];
  requestBody: KeyValueItem[];
  bearerToken?: string;
  username?: string;
  password?: string;
  apiKey?: string;
  questionSource?: string;
  requestFormat?: string;
  responseFormat?: string;
  samplePayload?: string;
  httpMethod?: HttpMethodId;
  /** Legacy single fields — still accepted and folded into lists on save */
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: string;
  scope?: string;
  headerName?: string;
}

const pushIf = (
  list: PAConfigurationKeyValueApi[],
  keyType: string,
  key: string,
  value?: string,
) => {
  if (value === undefined || value === null || value === "") return;
  list.push({ keyType, key, value });
};

const pushItems = (
  list: PAConfigurationKeyValueApi[],
  keyType: string,
  items: KeyValueItem[],
) => {
  items.forEach((item) => {
    if (!item.key.trim()) return;
    pushIf(list, keyType, item.key.trim(), item.value);
  });
};

export const buildSaveRequest = (
  providerId: number,
  stageKey: IntegrationTypeParam,
  form: EndpointFormPayload,
  authTypes: AuthTypeApi[],
  auditedBy: string,
  configurationTypeId?: number,
): SavePAConfigurationRequest => {
  const authTypeId = mapAuthTypeIdToApiId(form.authType, authTypes);
  if (!authTypeId) {
    throw new Error("Authentication type is not available. Refresh and try again.");
  }
  const credentials: PAConfigurationKeyValueApi[] = [];
  const details: PAConfigurationKeyValueApi[] = [];
  pushItems(credentials, KEY_TYPE.RequestUrl, form.requestUrls);
  pushItems(credentials, KEY_TYPE.QueryParam, form.queryParams);
  pushItems(credentials, KEY_TYPE.RequestBody, form.requestBody);
  // Connection headers → details; credential headers → credentials.
  pushItems(details, KEY_TYPE.Header, form.headers);
  pushItems(credentials, KEY_TYPE.Header, form.credentialHeaders || []);
  // Method belongs in credentials (not details).
  pushIf(credentials, KEY_TYPE.Method, DETAIL_KEY.Method, form.httpMethod || "GET");
  const hasRequestUrlKey = (key: string) =>
    form.requestUrls.some((i) => i.key.toLowerCase() === key.toLowerCase());
  const hasBodyKey = (key: string) =>
    form.requestBody.some((i) => i.key.toLowerCase() === key.toLowerCase());
  if (form.tokenUrl && !hasRequestUrlKey(REQUEST_URL_KEY.TokenUrl)) {
    pushIf(credentials, KEY_TYPE.RequestUrl, REQUEST_URL_KEY.TokenUrl, form.tokenUrl);
  }
  if (form.clientId && !hasBodyKey(REQUEST_BODY_KEY.ClientId)) {
    pushIf(credentials, KEY_TYPE.RequestBody, REQUEST_BODY_KEY.ClientId, form.clientId);
  }
  if (form.clientSecret && !hasBodyKey(REQUEST_BODY_KEY.ClientSecret)) {
    pushIf(
      credentials,
      KEY_TYPE.RequestBody,
      REQUEST_BODY_KEY.ClientSecret,
      form.clientSecret,
    );
  }
  if (form.scope && !hasBodyKey(REQUEST_BODY_KEY.Scope)) {
    pushIf(credentials, KEY_TYPE.RequestBody, REQUEST_BODY_KEY.Scope, form.scope);
  }
  pushIf(credentials, KEY_TYPE.Authentication, AUTH_KEY.BearerToken, form.bearerToken);
  pushIf(credentials, KEY_TYPE.Authentication, AUTH_KEY.Username, form.username);
  pushIf(credentials, KEY_TYPE.Authentication, AUTH_KEY.Password, form.password);
  // API Key auth: keyType "Authentication", key = header name, value = secret.
  if (form.headerName?.trim() && form.apiKey) {
    pushIf(credentials, KEY_TYPE.Authentication, form.headerName.trim(), form.apiKey);
  }
  // PAS format fields use keyType PASformats (not Detail). SamplePayload is UI-only — do not save.
  if (stageKey.toLowerCase() === "pas") {
    pushIf(details, KEY_TYPE.PASformats, DETAIL_KEY.RequestFormat, form.requestFormat);
    pushIf(details, KEY_TYPE.PASformats, DETAIL_KEY.ResponseFormat, form.responseFormat);
  } else {
    pushIf(details, KEY_TYPE.Detail, DETAIL_KEY.RequestFormat, form.requestFormat);
    pushIf(details, KEY_TYPE.Detail, DETAIL_KEY.ResponseFormat, form.responseFormat);
  }
  return {
    providerId,
    integrationType: stageKey.toUpperCase(),
    endpointUrl: form.endpoint,
    authTypeId,
    configurationTypeId,
    credentials,
    details,
    createdBy: auditedBy,
    updatedBy: auditedBy,
  };
};

export const saveStageConfig = async (
  providerId: number,
  stageKey: IntegrationTypeParam,
  form: EndpointFormPayload,
  authTypes: AuthTypeApi[],
  auditedBy: string,
  configurationTypeId?: number,
): Promise<PAConfigurationApi> => {
  const body = buildSaveRequest(
    providerId,
    stageKey,
    form,
    authTypes,
    auditedBy,
    configurationTypeId,
  );
  return ProviderIntegrationApi.saveIntegration(body);
};

export const formatTestResultMessage = (api: PAConfigurationApi) => {
  const test = api.testResult;
  if (!test) return "Saved · No test result";
  return buildTestResultDisplayMessage(test);
};